import { Link } from "@inertiajs/react"
import { PipeStatusBadge } from "@/components/projects/pipe-status-badge"
import { PROPERTY_TYPE_CONFIG, STATUS_CONFIG, STATUS_BASE_URLS } from "@/lib/project-configs"
import { Button } from "@/components/ui/button"
import { MapPin, Ruler, Home, User, ArrowUpRight, X } from "lucide-react"
import type { PipeStatusRecord, ProjectStatus } from "@/types"

export type ProjectMapPopupData = {
  id: string
  name: string
  status?: string
  pipe_status: PipeStatusRecord | null
  address: string | null
  property_type: string | null
  zip_code?: string | null
  city_name?: string | null
  lead_score?: number
  house_surface?: number | string | null
  field_surface?: number | string | null
  first_owner?: { display_name: string; phone_number: string | null; email: string | null } | null
  assigned_users?: { id: string; first_name: string; last_name: string }[]
  project_tags?: { id: string; name: string; color: string }[]
}

type Props = {
  project: ProjectMapPopupData
  onClose?: () => void
}

function formatSurface(val: number | string | null | undefined): string | null {
  if (val == null || val === "") return null
  const n = typeof val === "string" ? parseFloat(val) : val
  if (isNaN(n) || n <= 0) return null
  return n.toLocaleString("fr-FR")
}

export function ProjectMapPopup({ project, onClose }: Props) {
  const ptConfig = project.property_type ? PROPERTY_TYPE_CONFIG[project.property_type] : null
  const PropertyIcon = ptConfig?.icon

  const fieldSurface = formatSurface(project.field_surface)
  const houseSurface = formatSurface(project.house_surface)
  const hasSurfaces = fieldSurface || houseSurface

  const location = [project.address, [project.zip_code, project.city_name].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ")

  const hasTags = project.project_tags && project.project_tags.length > 0
  const hasOwner = project.first_owner?.display_name

  return (
    <div className="w-72" style={{ fontSize: 13, lineHeight: 1.45 }}>
      {/* Header: status + score + close */}
      <div className="flex items-center gap-2 mb-2">
        <div className="flex items-center gap-1.5">
          {project.pipe_status && <PipeStatusBadge pipeStatus={project.pipe_status} />}
          {ptConfig && (
            <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-600">
              {PropertyIcon && <PropertyIcon className="size-3" />}
              {ptConfig.label}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 ml-auto shrink-0">
          {project.lead_score != null && project.lead_score > 0 && (
            <LeadScoreIcon score={project.lead_score} status={project.status} />
          )}
          {onClose && (
            <Button
              variant="ghost"
              size="icon"
              className="size-5 rounded-full"
              onClick={onClose}
            >
              <X className="size-3" />
            </Button>
          )}
        </div>
      </div>

      {/* Name */}
      <Link
        href={`${STATUS_BASE_URLS[project.status as keyof typeof STATUS_BASE_URLS] || "/projects"}/${project.id}`}
        className="block text-sm font-semibold text-slate-900 leading-snug hover:text-blue-700 transition-colors"
      >
        {project.name}
      </Link>

      {/* Location */}
      {location && (
        <div className="flex items-start gap-1.5 mt-1.5 text-[11px] text-slate-500 leading-tight">
          <MapPin className="size-3 mt-px shrink-0 text-slate-400" />
          <span>{location}</span>
        </div>
      )}

      {/* Surfaces */}
      {hasSurfaces && (
        <div className="flex items-center gap-3 mt-2 pt-2 border-t border-slate-100">
          {fieldSurface && (
            <div className="flex items-center gap-1 text-[11px] text-slate-600">
              <Ruler className="size-3 text-slate-400" />
              <span>
                <span className="font-medium">{fieldSurface}</span>
                <span className="text-slate-400"> m² terrain</span>
              </span>
            </div>
          )}
          {houseSurface && (
            <div className="flex items-center gap-1 text-[11px] text-slate-600">
              <Home className="size-3 text-slate-400" />
              <span>
                <span className="font-medium">{houseSurface}</span>
                <span className="text-slate-400"> m² bâti</span>
              </span>
            </div>
          )}
        </div>
      )}

      {/* Owner */}
      {hasOwner && (
        <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-slate-100 text-[11px] text-slate-600">
          <User className="size-3 text-slate-400" />
          <span>{project.first_owner!.display_name}</span>
        </div>
      )}

      {/* Tags */}
      {hasTags && (
        <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-slate-100">
          {project.project_tags!.map((tag) => (
            <span
              key={tag.id}
              className="inline-block rounded-full px-2 py-px text-[10px] font-medium leading-relaxed"
              style={{
                backgroundColor: tag.color + "22",
                color: tag.color,
              }}
            >
              {tag.name}
            </span>
          ))}
        </div>
      )}

      {/* Action */}
      <Link
        href={`${STATUS_BASE_URLS[project.status as keyof typeof STATUS_BASE_URLS] || "/projects"}/${project.id}`}
        className="group flex items-center gap-1 mt-2.5 pt-2 border-t border-slate-100 text-[11px] font-medium text-blue-600 hover:text-blue-800 transition-colors"
      >
        Voir le dossier
        <ArrowUpRight className="size-3 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </Link>
    </div>
  )
}

function LeadScoreIcon({ score, status }: { score: number; status?: string }) {
  const scoreColor = score <= 3 ? "#ef4444" : score <= 6 ? "#f59e0b" : "#22c55e"
  const statusCfg = status ? STATUS_CONFIG[status as ProjectStatus] : null
  const Icon = statusCfg?.icon

  return (
    <div className="flex items-center gap-1" title={`Score ${score}/10`}>
      {Icon && <Icon className="size-3.5 shrink-0" style={{ color: scoreColor }} />}
      <span className="text-[10px] font-semibold tabular-nums" style={{ color: scoreColor }}>
        {score}/10
      </span>
    </div>
  )
}
